import Big from 'big.js';

import { sentryCaptureError } from '../../services/sentry/capture';

type Numeric = number | string | Big;

export const normalizedStringValue = (val: string) => {
  if (val.match(/[eE]/)) {
    try {
      return new Big(val).toFixed();
    } catch {
      return val;
    }
  }
  return val;
};

export const detectDigits = (num: string | number = 0) => {
  const [, right] = String(num).split('.');

  if (right) return right.length;

  return 0;
};

export const toBig = (value: Numeric): Big => {
  if (value && typeof value === 'object' && 'times' in value) return value as Big;
  try {
    return new Big(value);
  } catch {
    sentryCaptureError(`[Math Utils] Invalid numeric value: ${value}`);
    return new Big(0);
  }
};

export function truncateNumber(
  number: string | number | null | Big | undefined,
  decimalPlaces = 0,
  rounding: 'up' | 'down' = 'down',
): number {
  if (number === undefined || number === null) return 0;

  try {
    const bigNumber = toBig(number);
    const precision = decimalPlaces !== undefined ? decimalPlaces : detectDigits(bigNumber.toFixed());
    const multiplier = new Big(10).pow(precision);

    let result: Big;

    if (rounding === 'up') {
      result = bigNumber.times(multiplier).round(0, 3); // RoundingMode.CEIL
    } else {
      result = bigNumber.times(multiplier).round(0, 0); // RoundingMode.FLOOR
    }

    return result.div(multiplier).toNumber();
  } catch {
    return number as number;
  }
}

export const multiply = (a: Numeric, b: Numeric): Big => toBig(a).times(toBig(b));

export const divide = (a: Numeric, b: Numeric): Big => {
  if (toBig(b).eq(0)) {
    sentryCaptureError('Math utility error: division by zero');
    return toBig(0);
  }
  return toBig(a).div(toBig(b));
};

export const sum = (values: Numeric[]): Big => values.reduce<Big>((acc, val) => acc.plus(toBig(val)), new Big(0));

export const subtract = (a: Numeric, b: Numeric): Big => toBig(a).minus(toBig(b));

export const percentage = (amount: Numeric, percent: Numeric): Big => toBig(amount).times(toBig(percent)).div(100);
